import { ReactiveController, ReactiveControllerHost } from 'lit';

export default class ScrollToTargetController implements ReactiveController {
  private host: ReactiveControllerHost & Element;
  /**
   * Wrapper element that contains the scrollable area.
   * If not provided, the entire document will be used for scrolling.
   * @private
   */
  private wrapper?: HTMLElement;

  private scrollIntoViewOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'start', // Adjust the vertical alignment as needed ('start', 'center', 'end', 'nearest')
    inline: 'nearest', // Adjust the horizontal alignment if needed
  };

  constructor(host: ReactiveControllerHost & Element, wrapper?: HTMLElement) {
    this.host = host;
    this.wrapper = wrapper;
    this.host.addController(this);
  }

  hostConnected() {
    // Add event listeners if needed (e.g., click event for scrolling)
  }

  hostDisconnected() {
    // Clean up event listeners
  }

  scrollToTarget(target: HTMLElement, options?: ScrollIntoViewOptions): void {
    if (!target) {
      console.warn('Target element not provided for smooth scrolling.');
      return;
    }

    const finalOptions = { ...this.scrollIntoViewOptions, ...options };

    if (this.wrapper) {
      // Save scroll positions of potential parent scrollable elements
      const scrollPositions = this.captureParentScrollPositions();

      // Calculate distance relative to the wrapper
      const targetRect = target.getBoundingClientRect();
      const wrapperRect = this.wrapper.getBoundingClientRect();
      const scrollOffset = this.wrapper.scrollTop;
      const top = targetRect.top - wrapperRect.top + scrollOffset;

      // Perform the scrolling
      this.wrapper.scrollTo({
        top,
        behavior: finalOptions.behavior,
      });

      // Restore parent scroll positions to prevent dialog/parent containers from scrolling
      this.restoreParentScrollPositions(scrollPositions);
    } else {
      // If no wrapper provided, still prevent parent scrolling
      const scrollPositions = this.captureParentScrollPositions(target);
      target.scrollIntoView(finalOptions);
      this.restoreParentScrollPositions(scrollPositions);
    }
  }

  /**
   * Captures the current scroll position of all scrollable parent elements
   * @private
   */
  private captureParentScrollPositions(startElement?: HTMLElement): Array<[HTMLElement, number]> {
    const scrollPositions: Array<[HTMLElement, number]> = [];
    let element = startElement || this.wrapper;

    if (!element) return scrollPositions;

    // Get all scrollable parents
    let parent = element.parentElement;
    while (parent) {
      if (this.isScrollable(parent)) {
        scrollPositions.push([parent, parent.scrollTop]);
      }
      parent = parent.parentElement;
    }

    return scrollPositions;
  }

  /**
   * Restores the scroll positions of parent elements
   * @private
   */
  private restoreParentScrollPositions(positions: Array<[HTMLElement, number]>): void {
    if (positions.length === 0) return;

    // Use setTimeout to ensure the restoration happens after browser's own scrolling
    setTimeout(() => {
      positions.forEach(([element, position]) => {
        element.scrollTop = position;
      });
    }, 0);
  }

  /**
   * Determines if an element is scrollable
   * @private
   */
  private isScrollable(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    const overflow = style.getPropertyValue('overflow');
    const overflowY = style.getPropertyValue('overflow-y');

    // Check if element has scrollable content
    const hasScroll = element.scrollHeight > element.clientHeight;
    const isScrollable = overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll';

    return hasScroll && isScrollable;
  }

  scrollToTop(scrollableWrapper: HTMLElement): void {
    scrollableWrapper.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  scrollToBottom(scrollableWrapper: HTMLElement): void {
    scrollableWrapper.scrollTo({
      top: scrollableWrapper.scrollHeight,
      behavior: 'smooth',
    });
  }
}
